import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiCheckCircle, FiUpload } from "react-icons/fi";
import { useQuery, useQueryClient } from "react-query";
import {
  TARGET_LOOKUP,
  TARGET_OPTIONS,
  formatDateTime,
} from "../components/banners/BannerCard";
import { BANNERS_ADMIN } from "../config/apiConfig";
import axios from "../config/axiosInstance";
import { Alert, Button } from "flowbite-react";
import Cropper from "react-easy-crop";

const defaultFormState = {
  title: "",
  description: "",
  startDate: "",
  endDate: "",
  targetAudience: TARGET_OPTIONS[0].value,
  imagePreview: "",
  imageFile: null,
  showTitle: true,
};

const formatDatetimeForInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offsetMs = parsed.getTimezoneOffset() * 60000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
};

const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024; // 1MB

const BannerCreationPage = () => {
  const { bannerId } = useParams();
  const isEditing = Boolean(bannerId);
  const [formData, setFormData] = useState(defaultFormState);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [lastCreatedBanner, setLastCreatedBanner] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormHydrated, setIsFormHydrated] = useState(false);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [isCropReady, setIsCropReady] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const showSuccessCard = Boolean(
    successMessage && !isEditing && lastCreatedBanner
  );
  const showForm = !showSuccessCard;
  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
    setIsCropReady(Boolean(areaPixels?.width && areaPixels?.height));
  }, []);

  const createImage = (src) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = (error) => reject(error);
      image.setAttribute("crossOrigin", "anonymous");
      image.src = src;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 1200;
    canvas.height = 200;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Falha ao gerar a imagem recortada."));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.92
      );
    });
  };

  const {
    data: bannerToEdit,
    isLoading: isBannerLoading,
    error: bannerFetchError,
  } = useQuery(
    ["adminBanner", bannerId],
    async () => {
      if (!bannerId) return null;
      const { data } = await axios.get(`${BANNERS_ADMIN.GET(bannerId)}`);
      return data.data;
    },
    {
      enabled: isEditing,
      retry: false,
    }
  );

  useEffect(() => {
    setIsFormHydrated(false);
    setSuccessMessage("");
    if (!bannerId) {
      setFormData(defaultFormState);
      setFormErrors({});
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [bannerId]);

  useEffect(() => {
    return () => {
      if (cropImageSrc) {
        URL.revokeObjectURL(cropImageSrc);
      }
    };
  }, [cropImageSrc]);

  useEffect(() => {
    if (!isEditing || !bannerToEdit || isFormHydrated) return;
    setFormData({
      title: bannerToEdit.title || "",
      description: bannerToEdit.description || "",
      startDate: formatDatetimeForInput(bannerToEdit.startDate),
      endDate: formatDatetimeForInput(bannerToEdit.endDate),
      targetAudience: bannerToEdit.targetAudience || TARGET_OPTIONS[0].value,
      imagePreview: bannerToEdit.imageUrl || "",
      imageFile: null,
      showTitle:
        bannerToEdit.showTitle === false || bannerToEdit.showTitle === 0
          ? false
          : true,
    });
    setFormErrors({});
    setSuccessMessage("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsFormHydrated(true);
  }, [bannerToEdit, isEditing, isFormHydrated]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        imageFile: null,
        imagePreview: "",
      }));
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next.image;
        return next;
      });
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setFormErrors((prev) => ({
        ...prev,
        image: "A imagem deve ter no máximo 1MB.",
      }));
      setFormData((prev) => ({
        ...prev,
        imageFile: null,
        imagePreview: "",
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setCropImageSrc(objectUrl);
    setPendingFile(file);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
    setIsCropModalOpen(true);
    setIsCropReady(false);
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.image;
      return next;
    });
  };

  const handleCancelCrop = () => {
    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
    }
    setCropImageSrc("");
    setPendingFile(null);
    setIsCropModalOpen(false);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
    setIsCropReady(false);
    setIsCropping(false);
  };

  const handleApplyCrop = async () => {
    if (!cropImageSrc || !croppedAreaPixels || !pendingFile) {
      setFormErrors((prev) => ({
        ...prev,
        image:
          "Aguarde o carregamento completo da imagem antes de aplicar o recorte.",
      }));
      return;
    }
    setIsCropping(true);
    try {
      const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], pendingFile.name, {
        type: croppedBlob.type,
      });
      const previewUrl = URL.createObjectURL(croppedBlob);
      setFormData((prev) => ({
        ...prev,
        imageFile: croppedFile,
        imagePreview: previewUrl,
      }));
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next.image;
        return next;
      });
      handleCancelCrop();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error(error);
      setFormErrors((prev) => ({
        ...prev,
        image: "Não foi possível recortar a imagem. Tente novamente.",
      }));
    } finally {
      setIsCropping(false);
    }
  };

  const handleCreateAnother = () => {
    setSuccessMessage("");
    setLastCreatedBanner(null);
    setFormData(defaultFormState);
    setFormErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = "Defina o título do banner.";
    }
    const startTime = new Date(formData.startDate).getTime();
    const endTime = new Date(formData.endDate).getTime();
    if (!formData.startDate || Number.isNaN(startTime)) {
      errors.startDate = "Informe a data de início.";
    }
    if (!formData.endDate || Number.isNaN(endTime)) {
      errors.endDate = "Informe a data de término.";
    }
    if (startTime && endTime && startTime >= endTime) {
      errors.endDate = "A data final deve ser posterior à inicial.";
    }
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      setSuccessMessage("");
      return;
    }

    const formPayload = new FormData();
    formPayload.append("title", formData.title.trim());
    formPayload.append("description", formData.description.trim());
    formPayload.append("startDate", formData.startDate);
    formPayload.append("endDate", formData.endDate);
    formPayload.append("targetAudience", formData.targetAudience);
    formPayload.append("showTitle", formData.showTitle ? "true" : "false");
    if (formData.imageFile) {
      formPayload.append("image", formData.imageFile);
    }

    try {
      setIsSubmitting(true);
      if (isEditing && bannerId) {
        await axios.put(`${BANNERS_ADMIN.UPDATE(bannerId)}`, formPayload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setSuccessMessage("Banner atualizado com sucesso.");
      } else {
        const payloadSnapshot = {
          title: formData.title.trim(),
          description: formData.description,
          startDate: formData.startDate,
          endDate: formData.endDate,
          targetAudience: formData.targetAudience,
          imagePreview: formData.imagePreview,
          showTitle: formData.showTitle,
        };
        await axios.post(`${BANNERS_ADMIN.CREATE}`, formPayload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setLastCreatedBanner(payloadSnapshot);
        setFormData(defaultFormState);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setSuccessMessage(
          "Banner criado com sucesso. Volte para a listagem para conferir."
        );
      }
      queryClient.invalidateQueries(["adminBanners"]);
      setFormErrors({});
    } catch (error) {
      console.error(error);
      setFormErrors((prev) => ({
        ...prev,
        submit:
          error.response?.data?.message ||
          (isEditing
            ? "Não foi possível atualizar o banner. Tente novamente."
            : "Não foi possível criar o banner. Tente novamente."),
      }));
      setSuccessMessage("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isInitialLoading = isEditing && isBannerLoading;

  if (isEditing && bannerFetchError) {
    return (
      <section>
        <Link
          to="/admin/banners"
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-white bg-gray-500"
        >
          Voltar à lista
        </Link>
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-6 text-sm font-medium text-red-700">
          Não foi possível carregar os dados deste banner. Tente novamente mais
          tarde.
        </div>
      </section>
    );
  }

  const targetLabel =
    lastCreatedBanner?.targetAudience &&
    TARGET_LOOKUP[lastCreatedBanner.targetAudience]?.label;
  const formattedStart = lastCreatedBanner
    ? formatDateTime(lastCreatedBanner.startDate)
    : "";
  const formattedEnd = lastCreatedBanner
    ? formatDateTime(lastCreatedBanner.endDate)
    : "";

  return (
    <section>
      <Link
        to="/admin/banners"
        className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-white bg-gray-500"
      >
        Voltar à lista
      </Link>
      {showSuccessCard && lastCreatedBanner && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-6 shadow-lg transition">
          <div className="flex items-center gap-3 text-emerald-900">
            <FiCheckCircle className="text-3xl text-emerald-600" />
            <div>
              <p className="text-lg font-semibold">{successMessage}</p>
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-600">
                Banner salvo
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              className="rounded-full bg-white/80 px-4 py-2 text-sm font-semibold text-emerald-800 shadow"
              onClick={handleCreateAnother}
            >
              Criar outro banner
            </Button>
          </div>
        </div>
      )}
      {showForm &&
        (isInitialLoading ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/90 p-8 text-center text-sm text-slate-500">
            Carregando os dados do banner...
          </div>
        ) : (
          <div className="grid gap-3 mt-3">
            <form
              onSubmit={handleSubmit}
              className="space-y-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              noValidate
            >
              <h2 className="text-base font-semibold text-slate-900">
                {isEditing ? "Editar banner" : "Dados do banner"}
              </h2>

              <div className="space-y-2">
                <label
                  htmlFor="bannerTitle"
                  className="text-sm font-semibold text-slate-700"
                >
                  Título
                </label>
                <input
                  id="bannerTitle"
                  name="title"
                  type="text"
                  maxLength={70}
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  placeholder="Ex: Novas funcionalidades liberadas"
                />
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  {formErrors.title && (
                    <p className="text-xs text-red-500">{formErrors.title}</p>
                  )}
                  <span className="ml-auto text-slate-400">
                    {formData.title.length}/70
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="showTitle"
                  type="checkbox"
                  checked={formData.showTitle}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      showTitle: event.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-600"
                />
                <label
                  htmlFor="showTitle"
                  className="text-sm font-semibold text-slate-700"
                >
                  Exibir título do banner
                </label>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="bannerDescription"
                  className="text-sm font-semibold text-slate-700"
                >
                  Descrição
                </label>
                <textarea
                  id="bannerDescription"
                  name="description"
                  rows={4}
                  maxLength={180}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  placeholder="Conteúdo rápido sobre o objetivo do anúncio."
                />
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  {formErrors.description && (
                    <p className="text-xs text-red-500">
                      {formErrors.description}
                    </p>
                  )}
                  <span className="ml-auto text-slate-400">
                    {formData.description.length}/180
                  </span>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="startDate"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Início da visibilidade
                  </label>
                  <input
                    id="startDate"
                    name="startDate"
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  />
                  {formErrors.startDate && (
                    <p className="text-xs text-red-500">
                      {formErrors.startDate}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="endDate"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Fim da visibilidade
                  </label>
                  <input
                    id="endDate"
                    name="endDate"
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  />
                  {formErrors.endDate && (
                    <p className="text-xs text-red-500">{formErrors.endDate}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="targetAudience"
                  className="text-sm font-semibold text-slate-700"
                >
                  Mostrar para
                </label>
                <select
                  id="targetAudience"
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                >
                  {TARGET_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">
                  {TARGET_LOOKUP[formData.targetAudience]?.description ||
                    TARGET_OPTIONS[0].description}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Imagem do banner
                </label>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="bannerImage"
                    className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400"
                  >
                    <FiUpload />
                    Selecionar arquivo
                  </label>
                  <span className="text-xs text-slate-500">
                    PNG, JPG ou WEBP recomendado 1200x400px. Tamanho máximo de
                    1MB.
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  id="bannerImage"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="sr-only"
                />
                {formData.imageFile && (
                  <p className="text-xs text-slate-600">
                    Selecionado: {formData.imageFile.name}
                  </p>
                )}
                {formErrors.image && (
                  <p className="text-xs text-red-500">{formErrors.image}</p>
                )}
              </div>

              <div className="flex flex-col md:flex-row justify-center mt-10 gap-3 md:items-center">
                <Link
                  to="/admin/banners"
                  className="rounded-lg border border-slate-200 px-10 py-3 text-sm text-white bg-gray-500"
                >
                  Cancelar
                </Link>
                <Button
                  type="submit"
                  className="bg-primary px-8"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? isEditing
                      ? "Atualizando..."
                      : "Salvando..."
                    : isEditing
                    ? "Salvar alterações"
                    : "Criar banner"}
                </Button>
              </div>

              {!showSuccessCard && successMessage && (
                <Alert color="success">{successMessage}</Alert>
              )}
              {formErrors.submit && (
                <Alert color="failure">
                  {formErrors.submit}
                </Alert>
              )}
            </form>
          </div>
        ))}
      {isCropModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm px-4 py-6">
          <div className="relative w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-slate-100 opacity-60" />
            <div className="relative z-10 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">
                  Ajustar imagem (1200x200)
                </h3>
                <button
                  onClick={handleCancelCrop}
                  className="text-sm text-slate-500 hover:text-slate-900"
                >
                  Fechar
                </button>
              </div>
              <div className="mt-4 h-72 w-full overflow-hidden rounded-2xl bg-slate-900 relative">
                <Cropper
                  className="absolute inset-0"
                  image={cropImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={6}
                  cropShape="rect"
                  showGrid
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
              <div className="mt-4 flex flex-col gap-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  Zoom
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(event) => setZoom(Number(event.target.value))}
                    className="h-1 flex-1 rounded-full bg-slate-300 accent-blue-600"
                  />
                </label>
                <p className="text-xs text-slate-500">
                  O corte final terá 1200x200 px. Use o zoom para enquadrar a
                  imagem e clique em “Aplicar recorte”.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCancelCrop}
                    className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-900"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleApplyCrop}
                    disabled={!isCropReady || isCropping}
                    className={`rounded-full px-5 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-white shadow transition ${
                      isCropReady
                        ? "bg-slate-900 hover:bg-slate-700"
                        : "bg-slate-400 cursor-not-allowed"
                    }`}
                  >
                    {isCropping ? "Aplicando..." : "Aplicar recorte"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BannerCreationPage;
